import { useState, type SubmitEventHandler } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { executeRecaptcha } from '@/lib/recaptcha';
import { isConnectivityFailure } from '@/lib/service-failure';
import { useSessionStore } from '@/store/session-store';
import { readTextField } from '@/utils/form-data';
import { toLoginErrorMessage } from '../utils/login-error';
import { isValidProviderId } from '../utils/provider-id';
import { readReturnPath } from '../utils/return-path';
import { useProviderLogin } from './use-provider-login';

export const INVALID_PROVIDER_ID_MESSAGE = 'Please enter a valid Tax ID/NPI.';
export const VERIFICATION_UNAVAILABLE_MESSAGE =
  'The request could not be verified. Please reload the page and try again.';

const RECAPTCHA_ACTION = 'provider_login';

interface LoginSubmit {
  readonly handleSubmit: SubmitEventHandler<HTMLFormElement>;
  readonly errorMessage: string | null;
  /** The backend could not be reached; the form shows the standardised banner instead (PP-028). */
  readonly serviceUnavailable: boolean;
  readonly isPending: boolean;
}

/**
 * Submit behaviour of the login form: local format check, reCAPTCHA v3 token, access request, and where
 * the provider lands once verified. The format check and the token are both client-side conveniences —
 * portal-backend re-validates the id and verifies the token against the secret key before deciding.
 */
export function useLoginSubmit(): LoginSubmit {
  const [localMessage, setLocalMessage] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();
  const returnPath = readReturnPath(useLocation().state);
  const signIn = useSessionStore((state) => state.signIn);
  const mutation = useProviderLogin();

  const requestAccess = async (providerId: string): Promise<void> => {
    setIsVerifying(true);
    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);
      mutation.mutate(
        { providerId, recaptchaToken },
        {
          onSuccess: (response) => {
            signIn(response.providerId, response.idMatchType);
            navigate(returnPath, { replace: true });
          },
        },
      );
    } catch {
      // The reason a challenge failed is never shown: it would tell an automated caller what to fix.
      setLocalMessage(VERIFICATION_UNAVAILABLE_MESSAGE);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const providerId = readTextField(new FormData(event.currentTarget), 'providerId').trim();

    if (!isValidProviderId(providerId)) {
      mutation.reset();
      setLocalMessage(INVALID_PROVIDER_ID_MESSAGE);
      return;
    }

    setLocalMessage(null);
    void requestAccess(providerId);
  };

  return {
    handleSubmit,
    errorMessage: localMessage ?? toLoginErrorMessage(mutation.error),
    // A local format complaint is about what was typed, so it takes precedence over an outage the
    // provider cannot act on.
    serviceUnavailable: localMessage === null && isConnectivityFailure(mutation.error),
    isPending: isVerifying || mutation.isPending,
  };
}
